import "./home.scss"; // Use .scss or .css based on your setup
import React from "react";
import { Helmet } from "react-helmet";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";

const columnImages = [
  [
    "/images/SV-M12-2.jpg",
    "/images/SV-M2-1.jpg",
    "/images/SV-M3-1.jpg",
    "/images/SV-M4-1.jpg",
    "/images/SV-M18-1.jpg",
    "/images/SV-M44-1.jpg",
    "/images/SV-M45-1.jpg",
    "/images/SV-M96-2.jpeg",
  ],
  [
    "/images/SV-N1-1.jpg",
    "/images/SV-N2-1.jpg",
    "/images/SV-N3-1.jpg",
    "/images/SV-N4-1.jpg",
    "/images/SV-N5-2.jpg",
    "/images/SV-N14-2.jpg",
    "/images/SV-N9-2.jpg",
    "/images/SV-N5-2.jpg",
  ],
  [
    "/images/SV-R9-1.jpg",
    "/images/SV-R11-1.jpg",
    "/images/SV-R3-1.jpg",
    "/images/SV-R4-1.jpg",
    "/images/SV-R5-1.jpg",
    "/images/SV-R12-1.jpg",
    "/images/SV-R6-1.jpg",
    "/images/SV-R10-1.jpg",
  ],
];

const galleryItems = [
  {
    id: 1,
    type: "photo",
    src: "/images/SV-M12-2.jpg",
    alt: "Gold Necklace Manufacturer Satish Vaidya",
    category: "Necklaces",
  },
  {
    id: 2,
    type: "video",
    src: "https://res.cloudinary.com/dvjbwb5ms/video/upload/v1759759031/Video-1_jmcuba.mp4",
    alt: "Jewellery Manufacturing Process",
    category: "Behind the Scenes",
  },
  {
    id: 3,
    type: "photo",
    src: "/images/SV-M21-2.jpg",
    alt: "HUID Hallmarked Gold Ring",
    category: "Rings",
  },
  {
    id: 4,
    type: "video",
    src: "https://res.cloudinary.com/dvjbwb5ms/video/upload/v1759759031/Video-2_mhkwzy.mp4",
    alt: "Vaidya Jewellers by Ashish Showcase",
    category: "Showcase",
  },
  {
    id: 5,
    type: "photo",
    src: "/images/SV-R11-1.jpg",
    alt: "Wholesale Gold Earrings",
    category: "Earrings",
  },
  {
    id: 6,
    type: "video",
    src: "https://res.cloudinary.com/dvjbwb5ms/video/upload/v1759759032/Video-3_xvcvfb.mp4",
    alt: "B2B Jeweller Testimonial",
    category: "Testimonials",
  },
  {
    id: 7,
    type: "photo",
    src: "/images/SV-N1-1.jpg",
    alt: "Designer Gold Bracelet",
    category: "Bracelets",
  },
  {
    id: 8,
    type: "video",
    src: "https://res.cloudinary.com/dvjbwb5ms/video/upload/v1759759031/Video-4_cgfp9e.mp4",
    alt: "New Gold Collection Satish Vaidya",
    category: "Collections",
  },
  {
    id: 9,
    type: "photo",
    src: "/images/SV-N9-2.jpg",
    alt: "Traditional Gold Mangalsutra",
    category: "Mangalsutras",
  },
];

const partnerLogos = [
  { src: "/images/WHPlogo.png", alt: "WHP Jewellers Partner" },
  { src: "/images/Rankalogo.png", alt: "Ranka Jewellers Partner" },
  { src: "/images/Shahikalalogo.png", alt: "Shahikala Jewellers Partner" },
  { src: "/images/Heramblogo.png", alt: "Herambh Jewellers Partner" },
  { src: "/images/HSGlogo.png", alt: "HSG Jewellers Partner" },
  { src: "/images/SaiLogo.png", alt: "Sai Jewellers Partner" },
];

const Home = () => {
  const duplicatedLogos = [...partnerLogos, ...partnerLogos, ...partnerLogos];

  // High-Level SEO Schema for Google Knowledge Graph & Local Ranking
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "JewelryStore",
    name: "Satish Vaidya Manufacturers of Gold Ornaments",
    alternateName: "Vaidya Jewellers by Ashish",
    url: "https://svgoldjewelry.com",
    description:
      "Leading gold jewellery manufacturer and wholesaler in Mumbai since 1992. Specializing in HUID Hallmarked Mangalsutras, Necklaces, Rings, and bespoke gold ornaments.",
    foundingDate: "1992",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Mumbai",
      addressRegion: "Maharashtra",
      addressCountry: "IN",
    },
  };

  return (
    <>
      {/* SEO META TAGS */}
      <Helmet>
        <title>Gold Jewellery Manufacturer & Wholesaler | Satish Vaidya</title>
        <meta
          name="description"
          content="Satish Vaidya (Vaidya Jewellers by Ashish) is a premium gold jewellery manufacturer and wholesaler in Mumbai. Explore our exquisite HUID hallmarked collections."
        />
        <meta
          name="keywords"
          content="Gold jewellery manufacturer, wholesaler, Satish Vaidya, Vaidya Jewellers by Ashish, Mumbai gold wholesale, Mangalsutra manufacturer, HUID hallmarked gold"
        />
        <link rel="canonical" href="https://svgoldjewelry.com/" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <section className="banner-section">
        <div className="content-container">
          <h1>
            Crafting Timeless Elegance <br /> in Every Piece of Gold
          </h1>
          <p>
            Your trusted partner in exquisite gold jewelry manufacturing and
            wholesale, delivering quality, precision, and beauty{" "}
            <b>since 1992</b>.
          </p>
          <div className="button-container">
            <button className="btn">
              <a href="https://wa.me/918655981117">Partner With Us</a>
            </button>
          </div>
        </div>
        <div className="slider-container">
          {columnImages.map((images, columnIndex) => (
            <div className="slider-column" key={columnIndex}>
              {images.map((image, index) => (
                <div key={index} className="slider-item">
                  <div className="img-container">
                    <img
                      src={image}
                      alt={`Satish Vaidya Gold Jewellery Design ${index + 1}`}
                    />
                  </div>
                </div>
              ))}
              {images.map((image, index) => (
                <div key={index + images.length} className="slider-item">
                  <div className="img-container">
                    <img
                      src={image}
                      alt={`Satish Vaidya Gold Jewellery Design ${index + 1}`}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="about-section">
        <div className="partner-container container">
          <div className="content-box">
            <h2 className="section-title">Trusted by Reputed Jewellers</h2>
            <p className="section-description">
              We take pride in being the manufacturing partner for some of the
              most renowned names in the jewelry industry. Our commitment to
              quality, craftsmanship, and timely delivery has earned the trust
              of esteemed jewellers like Ranka Jewellers, Sai Jewellers, and
              Herambh Jewellers. Together, we craft timeless elegance and ensure
              unmatched satisfaction for their customers.
            </p>
          </div>

          <div className="logo-swiper-wrapper">
            <div className="logo-swiper">
              {duplicatedLogos.map((logo, index) => (
                <div key={index} className="img-container">
                  <img src={logo.src} alt={logo.alt} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="about-container">
          <div className="masonry-grid fixed-3x3">
            {galleryItems.map((item) => (
              <div key={item.id} className="masonry-item">
                {item.type === "photo" ? (
                  <img src={item.src} alt={item.alt} loading="lazy" />
                ) : (
                  <video
                    src={item.src}
                    autoPlay
                    loop
                    muted
                    playsInline
                    loading="lazy"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="about-text">
            <p>
              At <b>Satish Vaidya Manufacturers of Gold Ornaments</b> (proudly
              operating as <b>Vaidya Jewellers by Ashish</b>), we specialize in
              crafting timeless gold jewelry pieces, including Mangalsutras,
              Necklaces, Pendants, Watti-Mani, Rings, Earrings, Bangles, and
              Bracelets. With a heritage of trust and artistry, we have been
              proudly serving our partners <b>Ranka Jewellers</b>,{" "}
              <b>Sai Jewellers</b>, and <b>Herambh Jewellers</b> with exquisite
              designs and unmatched quality.
            </p>
            <p>
              Our commitment to excellence is reflected in every piece we
              create, ensuring each product is <b>HUID Hallmarked</b> for
              authenticity and purity. Established in <b>1992</b>, we take pride
              in being a trusted wholesale partner in the jewelry industry.
            </p>
            <div className="button-container">
              <button className="btn">
                <a href="https://wa.me/918655981117">Contact Us</a>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* MOBILE ONLY: 3D Swiper */}
      <section className="collection-section">
        <div className="container">
          <h2>Our Exquisite Collection</h2>
          <p>
            Explore a stunning range of gold jewelry, crafted with precision and
            love.
          </p>

          <div className="three-d-slider">
            <Swiper
              effect={"coverflow"}
              grabCursor={true}
              centeredSlides={true}
              slidesPerView={"auto"}
              loop={true}
              coverflowEffect={{
                rotate: 50,
                stretch: 0,
                depth: 150,
                modifier: 1.5,
                slideShadows: true,
              }}
              pagination={{ clickable: true }}
              navigation={true}
              modules={[EffectCoverflow, Pagination, Navigation]}
              className="swiper-container"
            >
              <SwiperSlide>
                <a href="/collection/Mangalsutra">
                  <img
                    src="/images/mangalsutra.png"
                    alt="Wholesale Gold Mangalsutra Manufacturer"
                  />
                </a>
              </SwiperSlide>
              <SwiperSlide>
                <a href="/collection/Necklace">
                  <img
                    src="/images/necklace.png"
                    alt="Wholesale Gold Necklace Manufacturer"
                  />
                </a>
              </SwiperSlide>
              <SwiperSlide>
                <a href="/collection/Ring">
                  <img
                    src="/images/ring.png"
                    alt="Wholesale Gold Ring Manufacturer"
                  />
                </a>
              </SwiperSlide>
              <SwiperSlide>
                <a href="/collection/Fancy%20Mangalsutra">
                  <img
                    src="/images/fancy-mangalsutra.png"
                    alt="Fancy Gold Mangalsutra Designs"
                  />
                </a>
              </SwiperSlide>
              <SwiperSlide>
                <a href="/collection/Pendant">
                  <img src="/images/pendant.png" alt="Gold Pendant Wholesale" />
                </a>
              </SwiperSlide>
              <SwiperSlide>
                <a href="/collection/Watti%20Mani">
                  <img
                    src="/images/wattimani.png"
                    alt="Traditional Watti Mani Gold Jewellery"
                  />
                </a>
              </SwiperSlide>
            </Swiper>
          </div>
        </div>
      </section>

      {/* DESKTOP ONLY: Grid Collection */}
      <section className="collection-section-2">
        <h2>Our Exquisite Collection</h2>
        <p>
          Explore a stunning range of gold jewelry, crafted with precision and
          love.
        </p>
        <div className="collection-grid-container">
          <a href="/collection/Mangalsutra">
            <div className="card">
              <img
                src="/images/mangalsutra.png"
                alt="Wholesale Gold Mangalsutra Manufacturer"
              />
              <div className="card-text">
                <h1>Mangalsutra</h1>
                <p>Sacred Bonds in Modern Gold</p>
              </div>
            </div>
          </a>
          <a href="/collection/Necklace">
            <div className="card">
              <img
                src="/images/necklace.png"
                alt="Wholesale Gold Necklace Manufacturer"
              />
              <div className="card-text">
                <h1>Necklace</h1>
                <p>Timeless Elegance & Statement Pieces</p>
              </div>
            </div>
          </a>
          <a href="/collection/Fancy%20Mangalsutra">
            <div className="card">
              <img
                src="/images/fancy-mangalsutra.png"
                alt="Fancy Gold Mangalsutra Designs"
              />
              <div className="card-text">
                <h1>Fancy Mangalsutra</h1>
                <p>Contemporary Interpretations of Tradition</p>
              </div>
            </div>
          </a>
          <a href="/collection/Ring">
            <div className="card">
              <img
                src="/images/ring.png"
                alt="Wholesale Gold Ring Manufacturer"
              />
              <div className="card-text">
                <h1>Ring</h1>
                <p>Perfection for Every Finger</p>
              </div>
            </div>
          </a>
          <a href="/collection/Pendant">
            <div className="card">
              <img src="/images/pendant.png" alt="Gold Pendant Wholesale" />
              <div className="card-text">
                <h1>Pendant</h1>
                <p>Minimalist Charms to Bold Designs</p>
              </div>
            </div>
          </a>
          <a href="/collection/Watti%20Mani">
            <div className="card">
              <img
                src="/images/wattimani.png"
                alt="Traditional Watti Mani Gold Jewellery"
              />
              <div className="card-text">
                <h1>Watti Mani</h1>
                <p>The Heart of Traditional Indian Jewellery</p>
              </div>
            </div>
          </a>
        </div>
      </section>
    </>
  );
};

export default Home;
